import { AutoColumn, ColumnCenter } from '../Column'
import { ChainId, Token } from '@zeroexchange/sdk'
import { CloseIcon, CustomLightSpinner } from '../../theme/components'
import { ErrorIllustratiton, SuccessIllustation } from '../IllustationIcons'
import { ExternalLink, TYPE } from '../../theme'
import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import { ButtonPrimary } from '../Button'
import { CHAIN_LABELS } from '../../constants'
import Circle from '../../assets/images/blue-loader.svg'
import Modal from '../Modal'
import { RowBetween } from '../Row'
import { Text } from 'rebass'
import { getEtherscanLink } from '../../utils'
import { useActiveWeb3React } from '../../hooks'

const Wrapper = styled.div`
  width: 100%;
`
const Section = styled(AutoColumn)`
  padding: 24px;
`

const BottomSection = styled(Section)`
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
`

const ConfirmedIcon = styled(ColumnCenter)`
  padding: 60px 0;
`
const SuccessIcon = styled(ColumnCenter)`
  padding: 20px 0;
  max-height: 180px;
`
const Title = styled.h2`
  font-weight: bold;
  font-size: 32px;
`

const StyledLinkMetaMask = styled(TYPE.link)`
  cursor: pointer;
  transition: all ease 0.3s;
  color: #a7b1f4;
  padding: 0.5rem 1rem 0.5rem 1rem;
  border: 1px solid #a7b1f4;
  border-radius: 30px;
  :hover {
    filter: brightness(0.9);
  }
`

function ConfirmationPendingContent({ onDismiss, pendingText }: { onDismiss: () => void; pendingText: string }) {
  return (
    <Wrapper>
      <Section>
        <RowBetween>
          <div />
          <CloseIcon onClick={onDismiss} />
        </RowBetween>
        <ConfirmedIcon>
          <CustomLightSpinner src={Circle} alt="loader" size={'90px'} />
        </ConfirmedIcon>
        <AutoColumn gap="12px" justify={'center'}>
          <Text fontWeight={500} fontSize={20}>
            Waiting For Confirmation
          </Text>
          <AutoColumn gap="12px" justify={'center'}>
            <Text fontWeight={600} fontSize={14} color="" textAlign="center">
              {pendingText}
            </Text>
          </AutoColumn>
          <Text fontSize={12} color="#565A69" textAlign="center">
            Your tokens transfer has been successful.
          </Text>
        </AutoColumn>
      </Section>
    </Wrapper>
  )
}

function TransactionSubmittedContent({
  onDismiss,
  chainId,
  hash,
  outputToken,
  handleClickAddToken
}: {
  onDismiss: () => void
  hash: string | undefined
  chainId: ChainId
  outputToken?: Token | undefined
  handleClickAddToken?: (outputToken: Token) => Promise<void>
}) {
  const theme = useContext(ThemeContext)

  return (
    <Wrapper>
      <Section>
        <RowBetween>
          <div />
          <CloseIcon onClick={onDismiss} />
        </RowBetween>
        <SuccessIcon>
          <SuccessIllustation />
        </SuccessIcon>
        <AutoColumn gap="12px" justify={'center'}>
          <Title>Success!</Title>
          <Text fontWeight={600} fontSize={13} textAlign="center">
            Your tokens transfer has been successful.
          </Text>
          {outputToken && handleClickAddToken && (
            <StyledLinkMetaMask
              onClick={() => handleClickAddToken(outputToken)}
              fontWeight={600}
              fontSize={[13, 14, 16]}
              textAlign="center"
            >
              Add {outputToken.symbol} to MetaMask
            </StyledLinkMetaMask>
          )}

          {chainId && hash && (
            <ExternalLink href={getEtherscanLink(chainId, hash, 'transaction')}>
              <Text fontWeight={500} fontSize={14} color={theme.primary1}>
                {chainId && `View on ${CHAIN_LABELS[chainId]}`}
              </Text>
            </ExternalLink>
          )}
          <ButtonPrimary onClick={onDismiss} style={{ margin: '20px 0 0 0' }}>
            <Text fontWeight={500} fontSize={20}>
              Done
            </Text>
          </ButtonPrimary>
        </AutoColumn>
      </Section>
    </Wrapper>
  )
}

export function ConfirmationModalContent({
  title,
  bottomContent,
  onDismiss,
  topContent
}: {
  title: string
  onDismiss: () => void
  topContent: () => React.ReactNode
  bottomContent: () => React.ReactNode
}) {
  return (
    <Wrapper>
      <Section>
        <RowBetween>
          <Text fontWeight={500} fontSize={20}>
            {title}
          </Text>
          <CloseIcon onClick={onDismiss} />
        </RowBetween>
        {topContent()}
      </Section>
      <BottomSection gap="12px">{bottomContent()}</BottomSection>
    </Wrapper>
  )
}

export function TransactionErrorContent({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  const theme = useContext(ThemeContext)
  return (
    <Wrapper>
      <Section>
        <RowBetween>
          <Text fontWeight={500} fontSize={20}>
            Error
          </Text>
          <CloseIcon onClick={onDismiss} />
        </RowBetween>
        <AutoColumn style={{ marginTop: 10, padding: '2rem 0' }} gap="24px" justify="center">
          <ErrorIllustratiton />
          <Text fontWeight={500} fontSize={16} color={theme.red1} style={{ textAlign: 'center', width: '85%' }}>
            {message}
          </Text>
        </AutoColumn>
      </Section>
      <BottomSection gap="12px">
        <ButtonPrimary onClick={onDismiss}>Dismiss</ButtonPrimary>
      </BottomSection>
    </Wrapper>
  )
}

interface ConfirmationModalProps {
  isOpen: boolean
  onDismiss: () => void
  hash: string | undefined
  content: () => React.ReactNode
  attemptingTxn: boolean
  pendingText: string
  outputToken?: Token | undefined
  handleClickAddToken?: (outputToken: Token) => Promise<void>
}

export default function TransactionConfirmationModal({
  isOpen,
  onDismiss,
  attemptingTxn,
  hash,
  pendingText,
  content,
  outputToken,
  handleClickAddToken
}: ConfirmationModalProps) {
  const { chainId } = useActiveWeb3React()

  if (!chainId) return null

  // confirmation screen
  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      {attemptingTxn ? (
        <ConfirmationPendingContent onDismiss={onDismiss} pendingText={pendingText} />
      ) : hash ? (
        <TransactionSubmittedContent
          chainId={chainId}
          hash={hash}
          onDismiss={onDismiss}
          outputToken={outputToken}
          handleClickAddToken={handleClickAddToken}
        />
      ) : (
        content()
      )}
    </Modal>
  )
}
